import React, { useState, useRef, KeyboardEvent } from "react";
import { Box, Text, HStack } from "@chakra-ui/react";

export interface Tab {
  key: string;
  label: string;
  content: React.ReactNode;
}

export interface TabbedCardPageProps {
  tabs: Tab[];
  defaultKey?: string;
  ariaLabel?: string; // optional, for screen readers
}

const TabbedCardPage = ({ tabs, defaultKey, ariaLabel }: TabbedCardPageProps): JSX.Element => {
  const defaultIndex = tabs.findIndex((t) => t.key === defaultKey);
  const [activeIndex, setActiveIndex] = useState(defaultIndex >= 0 ? defaultIndex : 0);

  // Store tab elements so we can focus them with the keyboard
  const tabRefs = useRef<Array<HTMLElement | null>>([]);

  const focusTab = (index: number) => {
    const el = tabRefs.current[index];
    if (el) {
      el.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    const lastIndex = tabs.length - 1;

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown": {
        event.preventDefault();
        const next = index === lastIndex ? 0 : index + 1;
        setActiveIndex(next);
        focusTab(next);
        break;
      }
      case "ArrowLeft":
      case "ArrowUp": {
        event.preventDefault();
        const prev = index === 0 ? lastIndex : index - 1;
        setActiveIndex(prev);
        focusTab(prev);
        break;
      }
      case "Home": {
        event.preventDefault();
        setActiveIndex(0);
        focusTab(0);
        break;
      }
      case "End": {
        event.preventDefault();
        setActiveIndex(lastIndex);
        focusTab(lastIndex);
        break;
      }
      default:
        break;
    }
  };

  return (
    <Box>
      {/* Tabs header */}
      <HStack spacing="sp24" px="sp24" pt="sp24" alignItems="flex-end" role="tablist" aria-label={ariaLabel ?? "Content aware settings"}>
        {tabs.map((tab, index) => {
          const selected = index === activeIndex;

          return (
            <Box
              // render as a real button so it is tabbable
              as="button"
              type="button"
              key={tab.key}
              ref={(el: HTMLElement | null) => {
                tabRefs.current[index] = el;
              }}
              role="tab"
              aria-selected={selected}
              aria-controls={`tab-panel-${tab.key}`}
              id={`tab-${tab.key}`}
              tabIndex={0}
              onClick={() => setActiveIndex(index)}
              onKeyDown={(e: KeyboardEvent<HTMLElement>) => handleKeyDown(e, index)}
              px="sp16"
              py="sp12"
              borderTopLeftRadius={selected ? "6px" : "none"}
              borderTopRightRadius={selected ? "6px" : "none"}
              backgroundColor={selected ? "white" : ""}
              cursor="pointer"
              zIndex={selected ? 2 : 1}
              position="relative"
              transition="all 0.2s ease"
            >
              <Text fontWeight={selected ? "semibold" : "normal"} fontSize="md" color={selected ? "text.primary" : "text.subtlest"}>
                {tab.label}
              </Text>
            </Box>
          );
        })}
      </HStack>

      {/* Content card */}
      <Box
        backgroundColor="white"
        borderRadius="2xl"
        mt="-sp2"
        mx="sp24"
        p="0"
        position="relative"
        zIndex={1}
        role="tabpanel"
        id={`tab-panel-${tabs[activeIndex]?.key}`}
        aria-labelledby={`tab-${tabs[activeIndex]?.key}`}
      >
        {tabs[activeIndex]?.content}
      </Box>
    </Box>
  );
};

export default TabbedCardPage;
